import { Vector2 } from "../../common/vector2.js";
import { snapToNearestHapticStep } from "../../haptics/haptics_controller.js";
import { ColorScheme } from "./color_scheme.js";
import { ImgID } from "../../image_loader/img_id.js";
import { RoundedRectangle } from "./rounded_rectangle.js";
import { TransformOptions, UIElement } from "./ui_element.js";
import { UIImage } from "./ui_image.js";
import { snapToDevicePx } from "../dom_backend/dom_backend.js";

export interface VibrationStrengthSliderOptions
{
  initial_value: number;
  on_change: ( value: number ) => void;
  on_commit: ( value: number ) => void;
}

// Big drag slider for vibration strength, snapped to HAPTICS_STRENGTH_STEPS (0/25/50/
// 75/100%) rather than free-continuous: a full continuous range is more precision than
// anyone can reliably feel or reproduce, and the coarser platforms (Telegram/iOS style
// buckets, web duration scaling) already can't act on fine-grained values anyway.
//
// The track itself is the vibrations_progress_bar art asset (5 evenly-spaced dots,
// diameter == image height, edge dots inset by one radius - see the PNG). Per the
// reference design there is no fill bar and no "passed" highlighting - just a single
// small indicator dot sitting on top of the asset's dot at the current position,
// deliberately smaller than the asset's own dot so its dark rim still shows around it.
//
// Tracks the finger across the whole element (see onTouchStart/Move/End) rather than
// requiring a precise hit on the indicator - the same "claim the gesture, then follow
// it regardless of exact position" pattern UIScrollableContainer uses for drag-scrolling,
// just snapping to the nearest stop instead of a free scroll offset.
export class VibrationStrengthSlider extends UIElement
{
  // vibrations_progress_bar.png is 559x81. Measured from the asset's pixels: each hole is a
  // dark disc spanning y=2..72 (centre y=37, not the image midline 40.5 - the rows below it
  // are a white highlight lip), with horizontal centres at x = 40 + step * 478. The indicator
  // sits 1px right and down of that geometric centre: the hole's dark inner shadow covers
  // only its upper part, so on device the exact centre read as up-left of the hole.
  private static readonly ASSET_W = 559;
  private static readonly ASSET_H = 81;
  private static readonly ASSET_DOT_FIRST_X = 41;
  private static readonly ASSET_DOT_SPAN_X = 478;
  private static readonly ASSET_DOT_CENTER_Y = 38;
  private static readonly INDICATOR_RATIO = 0.6;
  // Fill mixed with ~25% white, ~8% of the diameter thick - sampled from the reference mock.
  private static readonly BORDER_ON = "#56D05A";
  private static readonly BORDER_OFF = "#D86060";
  private static readonly BORDER_RATIO = 0.08;

  private value: number;
  private readonly on_change: ( value: number ) => void;
  private readonly on_commit: ( value: number ) => void;

  private readonly knob_d: number;
  private readonly usable_w: number;

  private readonly indicator: RoundedRectangle;

  private is_dragging = false;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    options: VibrationStrengthSliderOptions,
    ui_options?: TransformOptions
  )
  {
    // height is whatever the caller passed for layout purposes, but the art asset has a
    // fixed aspect ratio - re-derive the actual drawn height from width so the baked-in
    // dots stay circular instead of being squashed to fit an arbitrary box.
    // Snapped to device pixels: the DOM backend snaps node sizes anyway, and a track squashed
    // by that rounding no longer has its holes where the indicator math expects them.
    const drawn_h = snapToDevicePx( width * VibrationStrengthSlider.ASSET_H / VibrationStrengthSlider.ASSET_W );

    super( ctx, x, y, width, drawn_h, false, false, ui_options );

    this.value = snapToNearestHapticStep( options.initial_value );
    this.on_change = options.on_change;
    this.on_commit = options.on_commit;

    this.knob_d = drawn_h;
    this.usable_w = Math.max( 1, width - this.knob_d );

    const track = new UIImage( ctx, 0, 0, width, drawn_h, ImgID.UI_VibrationsProgressBar );
    track.setClickable( false );
    this.addChild( track );

    const indicator_d = this.indicatorDiameter();
    this.indicator = new RoundedRectangle(
      ctx, 0, 0, indicator_d, indicator_d,
      indicator_d / 2, ColorScheme.settings_toggle_on,
      undefined, false,
      VibrationStrengthSlider.BORDER_ON, indicator_d * VibrationStrengthSlider.BORDER_RATIO
    );
    this.indicator.setClickable( false );
    this.addChild( this.indicator );

    this.applyValue();
  }

  // Even whole number of CSS px: the canvas path centres by Rect's floor(d/2) and the DOM
  // path by d/2 after snapping d to device px - only an even integer makes both agree,
  // otherwise every rounding pushed the dot right and down off the hole.
  private indicatorDiameter(): number
  {
    const raw = this.knob_d * VibrationStrengthSlider.INDICATOR_RATIO;
    return Math.max( 2, Math.round( raw / 2 ) * 2 );
  }

  private applyValue(): void
  {
    const indicator_d = this.indicatorDiameter();
    const scale_x = this.rect.getW() / VibrationStrengthSlider.ASSET_W;
    const scale_y = this.rect.getH() / VibrationStrengthSlider.ASSET_H;
    const center_x = ( VibrationStrengthSlider.ASSET_DOT_FIRST_X + this.value * VibrationStrengthSlider.ASSET_DOT_SPAN_X ) * scale_x;
    const center_y = VibrationStrengthSlider.ASSET_DOT_CENTER_Y * scale_y;
    const half = indicator_d / 2;

    this.indicator.setPixelPos( new Vector2( center_x - half, center_y - half ), true );

    const is_off = this.value === 0;
    this.indicator.setColor( is_off ? ColorScheme.settings_toggle_off : ColorScheme.settings_toggle_on );
    this.indicator.setBorder(
      is_off ? VibrationStrengthSlider.BORDER_OFF : VibrationStrengthSlider.BORDER_ON,
      indicator_d * VibrationStrengthSlider.BORDER_RATIO
    );
  }

  private setValueFromLocalX( local_x: number, fire: boolean ): void
  {
    const raw_t = Math.max( 0, Math.min( 1, ( local_x - this.knob_d / 2 ) / this.usable_w ) );
    const snapped = snapToNearestHapticStep( raw_t );
    const changed = snapped !== this.value;

    this.value = snapped;
    this.applyValue();

    if ( fire && changed )
      this.on_change( snapped );
  }

  public onTouchStart( p: Vector2, now: number ): boolean
  {
    const local_p = this.toLocal( p );
    const pad_x = this.knob_d * 0.6;
    const pad_y = this.rect.getH() * 0.6;

    if ( local_p.x < -pad_x || local_p.x > this.rect.getW() + pad_x
      || local_p.y < -pad_y || local_p.y > this.rect.getH() + pad_y )
      return false;

    this.is_dragging = true;
    this.setValueFromLocalX( local_p.x, true );
    return true;
  }

  public onTouchMove( p: Vector2, now: number ): void
  {
    if ( !this.is_dragging )
    {
      super.onTouchMove( p, now );
      return;
    }

    this.setValueFromLocalX( this.toLocal( p ).x, true );
  }

  public onTouchEnd( p: Vector2, now: number, p_global: Vector2 | null = null ): void
  {
    if ( this.is_dragging )
    {
      this.is_dragging = false;
      this.on_commit( this.value );
    }

    super.onTouchEnd( p, now, p_global );
  }

  public cancelTouch( now: number ): void
  {
    if ( this.is_dragging )
    {
      this.is_dragging = false;
      this.on_commit( this.value );
    }

    super.cancelTouch( now );
  }

  public getValue(): number
  {
    return this.value;
  }

  public getDrawnHeight(): number
  {
    return this.rect.getH();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "VibrationStrengthSlider";
  }
}
